import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CheckoutSuccessComponentDef } from '@df/checkout/checkout-success.component.def';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { IAddressData } from '@df/session/api/address-book/address';
import { Customer } from '@df/session/api/customer';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsOutletBlockComponent } from '../catalog/outlet-block/icms-outlet-block.component';
import { ExponeaService } from '../module/exponea/exponea.service';
import { SagepayCloudPaymentRenewPaymentComponent } from '../module/sagepay/cloud-payment/sagepay-cloud-payment-renew-payment.component';
import { ActionComponent } from '../ui/api/action.component';
import { ResultComponent } from '../ui/api/result.component';
import { InputWrapBodyDirective } from '../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../ui/form/input-wrap.component';
import { MuiInputDirective } from '../ui/form/mui-input.directive';
import { CheckoutSuccessContentBlockComponent } from './checkout-success-content-block.component';
import { CheckoutSuccessNewsletterComponent } from './checkout-success-newsletter.component';

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface ISuccessCustomerData {
            subscribed?: boolean;
        }
    }
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Api.Checkout.Success {
        interface ISuccessData {
            billing: IAddressData;
            shipping_method: IShippingMethod;
            shipping: IAddressData;
            payment_method: IPaymentMethod;
            sccp_payment_reference?: string;
        }

        interface ISuccessItemData {
            price: number;
        }

        interface IShippingMethod {
            code: string;
            description?: string;
        }

        interface IPaymentMethod {
            code: string | number;
            title: string;
            description?: string;
        }
    }
}

@Component({
    selector: 'checkout-success',
    styleUrl: './checkout-success.scss',
    templateUrl: './checkout-success.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ActionComponent,
        CheckoutSuccessContentBlockComponent,
        CheckoutSuccessNewsletterComponent,
        CommonModule,
        FormsModule,
        HrefComponent,
        ICmsOutletBlockComponent,
        IfSizeDirective,
        InputWrapBodyDirective,
        InputWrapComponent,
        MuiInputDirective,
        PriceComponent,
        ResultComponent,
        RouterModule,
        SagepayCloudPaymentRenewPaymentComponent,
        TranslatePipe
    ]
})
export class CheckoutSuccessComponent extends CheckoutSuccessComponentDef implements OnInit {
    protected exponeaConsentStatus = new Status();
    protected exponeaConsentResult: boolean = false;
    protected showForm = false;
    protected isRegistered = false;

    readonly client = inject(Client);
    protected readonly customer = inject(Customer);
    protected exponeaService = inject(ExponeaService);

    @ViewChild(CheckoutSuccessNewsletterComponent)
    private readonly checkoutSuccessNewsletterComponent?: CheckoutSuccessNewsletterComponent;

    override ngOnInit() {
        super.ngOnInit();

        this.checkExponeaConsent(this.data.customer.email);

        this.changeDetectorRef.detectChanges();
    }

    get paymentMethod(): string {
        return this.data.payment_method.title || this.data.payment_method.description || '';
    }

    protected get showSubscriptionOptIn(): boolean {
        return !this.exponeaConsentResult && !this.exponeaConsentStatus.busy;
    }

    protected get exponeaLink(): string | undefined {
        return this.customer.model.data?.exponea;
    }

    protected checkExponeaConsent(email: string): void {
        this.exponeaService.checkConsent(this.exponeaConsentStatus, email, result => {
            this.exponeaConsentResult = !!result.consent;

            this.changeDetectorRef.detectChanges();
        });
    }

    protected openForm(): void {
        this.showForm = true;
    }

    protected override onRegister() {
        this.isRegistered = true;
    }

    override register(): void {
        if (this.checkoutSuccessNewsletterComponent) {
            this.checkoutSuccessNewsletterComponent.submit();
        } else {
            super.register();
        }
    }
}
